// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — onboarding logic. Pure: no DOM, no network.
//
// Four steps, one per screen, "because each can fail differently": tenant URL →
// discovery → sign-in → token. The whole flow runs in the browser tab, and the
// install prompt is deliberately withheld until there is a valid session, so
// nobody installs an app they cannot get into.
// ─────────────────────────────────────────────────────────────────────────────

import { type LoginMethod, type OnboardingStep, type TokenStepState } from "home/app/mobile/types";
import { currentLanguage, t } from "home/app/mobile/i18n";

/**
 * The host, as the user could plausibly have typed it.
 *
 * Accepts `https://demo.pqforce.com/rhb`, `DEMO.pqforce.com`, and a bare host;
 * returns the bare lowercase host. The alias (`/rhb`) is dropped here on
 * purpose — this value only decides which INSTALLATION to ask about, and
 * discovery is a property of the server. The full URL the user typed is kept by
 * the caller and sent whole, because the tenant alias is what
 * `ACM/ApiToken/Create?tenantalias=` needs.
 */
export function normaliseTenantHost(input: string): string {
    return input
        .trim()
        .replace(/^https?:\/\//i, "")
        .replace(/\/.*$/, "")
        .toLowerCase();
}

/**
 * Does this look like a host at all?
 *
 * ⚠ THIS IS A TYPO CATCHER, NOT A SECURITY CONTROL. It runs in the browser, so
 * it decides nothing: the portal re-validates the target server-side before it
 * will proxy anything to it — https only, public addresses only, no redirect
 * following. See §4 of the implementation plan. Anyone can skip this check by
 * calling the endpoint directly, and that must remain harmless.
 */
export function isValidTenantHost(host: string): boolean {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host);
}

/**
 * The inline error under the field. Shown on failure, cleared on the next keystroke.
 *
 * A function, not a constant: a module-level string would be read before the dictionary is loaded
 * and freeze the English — see `app/mobile/i18n.ts`.
 */
export function tenantHostError(): string {
    return t(
        "mobileSignInHostInvalid",
        "That does not look like a PQFORCE address. It should be a full host, like demo.pqforce.com.",
    );
}

/**
 * A display name for the installation, before discovery has told us its real one.
 *
 * The first label, capitalised — "demo.pqforce.com" → "Demo". A placeholder: the
 * moment discovery answers with a real `displayName`, that wins.
 */
export function tenantDisplayName(host: string): string {
    const first = host.split(".")[0] ?? "";
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : host;
}

/**
 * The four-segment progress rail.
 *
 * Four segments, seven states: the transient states sit with the step they
 * belong to, so the rail never flickers backwards while a request is in flight.
 * `failed` holds the rail where discovery left it — the user has not advanced.
 */
const RAIL_POSITION: Readonly<Record<OnboardingStep, number>> = {
    url: 0,
    discovering: 0,
    failed: 0,
    options: 1,
    login: 2,
    sso: 2,
    validating: 3,
    done: 3,
};

/** One boolean per segment: filled or empty. */
export function onboardingRail(step: OnboardingStep): boolean[] {
    const position = RAIL_POSITION[step] ?? 0;
    return [0, 1, 2, 3].map((i) => i <= position);
}

/**
 * Where to go once discovery answers.
 *
 * ⚠ "If discovery returns only interactive, skip this screen and go straight to
 * the form." A one-row chooser is a screen that asks a question with one answer.
 * With nothing offered at all there is nothing to choose either — that is the
 * failure path, not an empty list.
 */
export function stepAfterDiscovery(methods: readonly LoginMethod[]): OnboardingStep {
    if (methods.length === 0) return "failed";
    if (methods.length === 1) return stepForMethod(methods[0]!);
    return "options";
}

/**
 * Which screen a chosen method leads to.
 *
 * ⚠ Both kinds normally lead to the SAME place — the tenant's own authorization
 * page, awaited here. The branch is on `acceptsCredentials`, never on `kind`:
 * "interactive" describes where the person types their password, not who
 * collects it, and today the tenant always collects it. See the field's own note.
 */
export function stepForMethod(method: LoginMethod): OnboardingStep {
    return method.acceptsCredentials === true ? "login" : "sso";
}

/** "Two sign-in methods available." — built from what was actually offered. */
export function methodCountLabel(count: number): string {
    if (count === 1) return t("mobileSignInMethodCountOne", "One sign-in method available.");
    // English spells small counts out; German gets the figure, since a spelled-out English word
    // would land in the German sentence.
    const words = ["No", "One", "Two", "Three", "Four", "Five", "Six"];
    const word = currentLanguage() === "en" ? (words[count] ?? String(count)) : String(count);
    return t("mobileSignInMethodCountMany", "{count} sign-in methods available.", { count: word });
}

/** Back goes to the chooser from a sign-in screen, and to the address otherwise. */
export function stepBackFrom(step: OnboardingStep): OnboardingStep {
    return step === "login" || step === "sso" ? "options" : "url";
}

/**
 * The three token milestones.
 *
 * These are real steps against the tenant, not a progress animation:
 *   1. `POST /API/V2/ACM/ApiToken/Create?tenantalias=…`
 *   2. `GET  /API/V2/ACM/AuthorizedApiToken/{id}`
 *   3. `GET  /API/V2/ACM/CurrentUser`  (the profile, and `.resource.id`)
 * So a failure is surfaced at whichever one failed, rather than as one generic
 * error — that is the difference between "try again" and "your administrator
 * has not enabled the mobile endpoint".
 */
export function tokenStepLabels(host: string): string[] {
    return [
        t("mobileSignInStepRequestToken", "Requesting a bearer token"),
        t("mobileSignInStepValidate", "Validating it against {host}", { host }),
        t("mobileSignInStepProfile", "Loading your profile and projects"),
    ];
}

/**
 * The state of each milestone.
 *
 * `current` is the index in flight. `failedAt`, when set, marks that step failed
 * and leaves the ones after it pending — nothing ran.
 */
export function tokenStepStates(current: number, failedAt: number | null = null): TokenStepState[] {
    return [0, 1, 2].map((i) => {
        if (failedAt !== null && i === failedAt) return "failed";
        if (failedAt !== null && i > failedAt) return "pending";
        if (i < current) return "done";
        if (i === current) return "active";
        return "pending";
    });
}

/** The copy for a discovery failure, from the handoff's `#2b` card. */
/**
 * The heading for a failed onboarding attempt.
 *
 * ⚠ "That tenant did not answer" was the title for EVERY failure, and for the two commonest
 * it is false: a 403 and a 404 both mean the installation answered immediately and told us
 * something precise — its administrator has not switched the mobile app on, or the portal has no
 * tenant registered at that address. Both are portal-side configuration, and titling them as a
 * dead host sends the reader to check the network, the VPN, or the PQFORCE server's health.
 * That is exactly what happened on dev on 2026-08-23: a clean 403 was reported as "does not work".
 *
 * The body already carries the server's own sentence — `result` is the message the user sees — so
 * this only has to stop the heading contradicting it.
 */
export function onboardingFailureTitle(status: number | null): string {
    switch (status) {
        case 403:
            return t("mobileSignInFailNotEnabled", "Not switched on for this tenant");
        case 404:
            return t("mobileSignInFailUnknownAddress", "We do not know that address");
        case 503:
            return t("mobileSignInFailNotReady", "The mobile service is not ready yet");
        default:
            return t("mobileSignInFailNoAnswer", "That tenant did not answer");
    }
}

export function discoveryFailureText(host: string): string {
    return t(
        "mobileSignInDiscoveryFailed",
        "We reached {host} but it did not answer with login options. It may be an older version, or the mobile endpoint may be switched off. Your administrator can enable it.",
        { host },
    );
}
